import { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractAxiosError } from "@/lib/api";
import { toast } from "sonner";
import { Database, RefreshCw, Save, ChevronRight } from "lucide-react";

function previewDoc(doc, idField) {
    if (!doc) return "—";
    const id = doc[idField];
    const keys = ["email", "name", "action", "path", "status", "org_id", "pool_id", "identifier"];
    for (const key of keys) {
        if (doc[key]) return String(doc[key]);
    }
    return id || JSON.stringify(doc).slice(0, 48);
}

export function AdminDatabasePage() {
    const [collections, setCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [search, setSearch] = useState("");
    const [idField, setIdField] = useState("id");
    const [readOnly, setReadOnly] = useState(false);
    const [selectedDocId, setSelectedDocId] = useState(null);
    const [editorValue, setEditorValue] = useState("");
    const [loadingCollections, setLoadingCollections] = useState(true);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [loadingDoc, setLoadingDoc] = useState(false);
    const [saving, setSaving] = useState(false);

    const limit = 40;

    const loadCollections = useCallback(async () => {
        setLoadingCollections(true);
        try {
            const { data } = await api.get("/admin/collections");
            setCollections(data.collections || []);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load collections"));
        } finally {
            setLoadingCollections(false);
        }
    }, []);

    const loadDocuments = useCallback(async (collectionName, nextSkip = 0, term = search) => {
        if (!collectionName) return;
        setLoadingDocs(true);
        try {
            const { data } = await api.get(`/admin/data/${collectionName}`, {
                params: { skip: nextSkip, limit, search: term.trim() || undefined },
            });
            setDocuments(data.documents || []);
            setTotal(data.total || 0);
            setSkip(data.skip || 0);
            setIdField(data.id_field || "id");
            setReadOnly(Boolean(data.read_only));
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load documents"));
        } finally {
            setLoadingDocs(false);
        }
    }, [search]);

    const loadDocument = useCallback(async (collectionName, docId) => {
        if (!collectionName || !docId) return;
        setLoadingDoc(true);
        try {
            const { data } = await api.get(`/admin/data/${collectionName}/${encodeURIComponent(docId)}`);
            setEditorValue(JSON.stringify(data.document || {}, null, 2));
            setReadOnly(Boolean(data.read_only));
            setIdField(data.id_field || "id");
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load document"));
        } finally {
            setLoadingDoc(false);
        }
    }, []);

    useEffect(() => { loadCollections(); }, [loadCollections]);

    useEffect(() => {
        if (selectedCollection) {
            setSelectedDocId(null);
            setEditorValue("");
            loadDocuments(selectedCollection, 0);
        }
    }, [selectedCollection, loadDocuments]);

    const selectDocument = (doc) => {
        const docId = doc[idField];
        setSelectedDocId(docId);
        loadDocument(selectedCollection, docId);
    };

    const parsedEditor = useMemo(() => {
        try {
            return { ok: true, value: JSON.parse(editorValue) };
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }, [editorValue]);

    const saveDocument = async () => {
        if (!selectedCollection || !selectedDocId || readOnly) return;
        if (!parsedEditor.ok) {
            toast.error("Fix JSON before saving", { description: parsedEditor.error });
            return;
        }
        const updates = { ...parsedEditor.value };
        delete updates[idField];
        delete updates._id;

        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/data/${selectedCollection}/${encodeURIComponent(selectedDocId)}`, { updates });
            setEditorValue(JSON.stringify(data.document || {}, null, 2));
            toast.success("Document updated", { description: `Fields: ${(data.updated_fields || []).join(", ")}` });
            loadDocuments(selectedCollection, skip);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to save document"));
        } finally {
            setSaving(false);
        }
    };

    const selectedMeta = collections.find((c) => c.name === selectedCollection);

    return (
        <div className="space-y-4" data-testid="admin-database">
            <div className="hidden lg:block">
                <h2 className="font-heading text-xl tracking-tight inline-flex items-center gap-2">
                    <Database className="admin-accent-icon h-5 w-5" /> Database browser
                </h2>
                <p className="mt-1 text-sm text-white/45">
                    Inspect MongoDB collections. Sensitive fields are masked; audit logs and billing events are read-only.
                </p>
            </div>

            <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)_minmax(0,1.1fr)]">
                <div className="surface max-h-[min(40vh,20rem)] overflow-auto p-2 lg:max-h-[640px]">
                    <p className="px-2 py-1.5 label-eyebrow">Collections</p>
                    {loadingCollections ? (
                        <p className="px-2 py-4 text-xs text-white/40">Loading…</p>
                    ) : (
                        <div className="space-y-0.5">
                            {collections.map((col) => (
                                <button
                                    key={col.name}
                                    type="button"
                                    onClick={() => setSelectedCollection(col.name)}
                                    className={`flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-xs transition-colors ${
                                        selectedCollection === col.name ? "bg-[#5d00ff]/15 text-white" : "text-white/65 hover:bg-white/[0.04]"
                                    }`}
                                >
                                    <span className="truncate">{col.label}</span>
                                    <span className="shrink-0 font-mono text-[10px] text-white/35">{col.count ?? "—"}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="surface flex max-h-[640px] flex-col overflow-hidden">
                    <div className="flex flex-wrap items-center gap-2 border-b border-white/[0.06] p-3">
                        <p className="text-sm font-medium text-white/85">{selectedMeta?.label || "Select a collection"}</p>
                        {selectedMeta?.read_only && (
                            <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-300">Read only</span>
                        )}
                        {selectedCollection && (
                            <>
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search…"
                                    className="input-dark ml-auto min-w-[8rem] flex-1 rounded-md px-2.5 py-1.5 text-xs sm:max-w-[12rem]"
                                />
                                <button
                                    type="button"
                                    onClick={() => loadDocuments(selectedCollection, 0, search)}
                                    className="btn-ghost rounded-md px-2 py-1.5 text-xs"
                                >
                                    Search
                                </button>
                                <button type="button" onClick={() => loadDocuments(selectedCollection, skip)} className="btn-ghost rounded-md p-1.5">
                                    <RefreshCw className={`h-3.5 w-3.5 ${loadingDocs ? "animate-spin" : ""}`} />
                                </button>
                            </>
                        )}
                    </div>
                    <div className="min-h-0 flex-1 overflow-auto">
                        {!selectedCollection ? (
                            <p className="p-6 text-sm text-white/40">Choose a collection to browse documents.</p>
                        ) : loadingDocs ? (
                            <p className="p-6 text-sm text-white/40">Loading documents…</p>
                        ) : (
                            <div className="divide-y divide-white/[0.04]">
                                {documents.map((doc) => {
                                    const docId = doc[idField];
                                    const active = docId === selectedDocId;
                                    return (
                                        <button
                                            key={docId || JSON.stringify(doc).slice(0, 24)}
                                            type="button"
                                            onClick={() => selectDocument(doc)}
                                            className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs hover:bg-white/[0.03] ${active ? "bg-[#5d00ff]/10" : ""}`}
                                        >
                                            <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${active ? "admin-accent-text" : "text-white/25"}`} />
                                            <span className="min-w-0 flex-1 truncate text-white/80">{previewDoc(doc, idField)}</span>
                                            <span className="shrink-0 font-mono text-[10px] text-white/30">{docId}</span>
                                        </button>
                                    );
                                })}
                                {!documents.length && <p className="p-6 text-sm text-white/40">No documents found.</p>}
                            </div>
                        )}
                    </div>
                    {selectedCollection && total > limit && (
                        <div className="flex items-center justify-between border-t border-white/[0.06] p-2 text-xs text-white/45">
                            <button type="button" disabled={skip <= 0} onClick={() => loadDocuments(selectedCollection, Math.max(0, skip - limit))} className="btn-ghost rounded px-2 py-1 disabled:opacity-40">Prev</button>
                            <span className="font-mono tabular-nums">{skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
                            <button type="button" disabled={skip + limit >= total} onClick={() => loadDocuments(selectedCollection, skip + limit)} className="btn-ghost rounded px-2 py-1 disabled:opacity-40">Next</button>
                        </div>
                    )}
                </div>

                <div className="surface flex max-h-[640px] flex-col overflow-hidden">
                    <div className="flex items-center justify-between gap-2 border-b border-white/[0.06] p-3">
                        <p className="text-sm font-medium text-white/85 truncate">{selectedDocId ? `Document · ${selectedDocId}` : "Document editor"}</p>
                        {selectedDocId && !readOnly && (
                            <button
                                type="button"
                                onClick={saveDocument}
                                disabled={saving || !parsedEditor.ok}
                                className="btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs disabled:opacity-50"
                            >
                                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save changes"}
                            </button>
                        )}
                    </div>
                    <div className="min-h-0 flex-1 p-3">
                        {!selectedDocId ? (
                            <p className="text-sm text-white/40">Select a document to view or edit JSON fields.</p>
                        ) : loadingDoc ? (
                            <p className="text-sm text-white/40">Loading document…</p>
                        ) : (
                            <>
                                {!parsedEditor.ok && (
                                    <p className="mb-2 text-xs text-rose-400">Invalid JSON: {parsedEditor.error}</p>
                                )}
                                <textarea
                                    value={editorValue}
                                    onChange={(e) => setEditorValue(e.target.value)}
                                    readOnly={readOnly}
                                    spellCheck={false}
                                    className="input-dark h-[calc(640px-8.5rem)] w-full resize-none rounded-md px-3 py-2 font-mono text-[11px] leading-relaxed"
                                />
                                {readOnly && <p className="mt-2 text-xs text-white/40">This collection is read-only in the admin console.</p>}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
